import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ContentSection } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteSection } from '../hooks/use-section-mutations';

export interface DeleteSectionDialogProps {
  storeId: string;
  row: ContentSection | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** Confirms deleting a section. Its blocks survive. */
export function DeleteSectionDialog({
  storeId,
  row,
  onOpenChange,
  onDeleted,
}: DeleteSectionDialogProps) {
  const { t } = useTranslation(['content', 'common', 'errors']);
  const remove = useDeleteSection(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('content:deleteSection.title', { name: row?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('content:deleteSection.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              event.preventDefault();
              if (row) {
                remove.mutate(row.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending
              ? t('content:deleteSection.submitting')
              : t('content:deleteSection.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
